import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, Wallet, Ruler, Scale, Percent, ArrowUpRight } from "lucide-react";

const gammes = [
  { label: "Économique", prix: 1400 },
  { label: "Standard", prix: 1800 },
  { label: "Haut de gamme", prix: 2500 },
];

const typesBien = [
  { label: "Ancien", taux: 0.075 },
  { label: "Neuf", taux: 0.025 },
  { label: "Terrain", taux: 0.08 },
];

const zonesPtz = ["A / A bis", "B1", "B2", "C"];

const plafondsOperation = {
  "A / A bis": [150000, 225000, 270000, 315000, 360000],
  "B1": [135000, 202500, 243000, 283500, 324000],
  "B2": [110000, 165000, 198000, 231000, 264000],
  "C": [100000, 150000, 180000, 210000, 240000],
};

const seuilsTranches = {
  "A / A bis": [25000, 31000, 37000, 49000],
  "B1": [21500, 26000, 30000, 34500],
  "B2": [18000, 22500, 27000, 31500],
  "C": [15000, 19500, 24000, 28500],
};

const coefFamilial = [1, 1.5, 1.8, 2.1, 2.4];

const typesLogement = [
  { label: "Maison individuelle", q: [0.3, 0.2, 0.2, 0.1] },
  { label: "Logement collectif", q: [0.5, 0.4, 0.4, 0.2] },
];

const fmt = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

function Slider({ label, value, min, max, step, onChange, format, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <label className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60">{label}</label>
        <span className="font-sub font-bold text-lg" data-testid={`${testid}-value`}>{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
        data-testid={testid}
      />
    </div>
  );
}

function ToggleGroup({ label, options, value, onChange, activeClass, testidPrefix }) {
  return (
    <div>
      <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-3">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const val = typeof opt === "string" ? opt : opt.label;
          const isActive = (typeof value === "string" ? value : value.label) === val;
          return (
            <button
              key={val}
              onClick={() => onChange(opt)}
              data-testid={`${testidPrefix}-${val.toLowerCase().replace(/[\s/]+/g, "-")}`}
              className={`font-sub text-sm px-4 py-2 rounded-full border transition-[background-color,color,border-color] duration-200 ${
                isActive ? activeClass : "border-ink/20 text-ink/70 hover:border-terra hover:text-terra"
              }`}
            >
              {val}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Calculators() {
  const [capital, setCapital] = useState(250000);
  const [taux, setTaux] = useState(3.5);
  const [duree, setDuree] = useState(20);

  const [mensuMax, setMensuMax] = useState(1400);
  const [taux2, setTaux2] = useState(3.5);
  const [duree2, setDuree2] = useState(20);
  const [apport, setApport] = useState(30000);

  const [surface, setSurface] = useState(120);
  const [gamme, setGamme] = useState(gammes[1]);

  const [prixAchat, setPrixAchat] = useState(250000);
  const [typeBien, setTypeBien] = useState(typesBien[2]);

  const [zonePtz, setZonePtz] = useState("B2");
  const [personnes, setPersonnes] = useState(4);
  const [rfrPtz, setRfrPtz] = useState(60000);
  const [coutOperation, setCoutOperation] = useState(280000);
  const [typeLogement, setTypeLogement] = useState(typesLogement[0]);

  const [inclurePtz, setInclurePtz] = useState(true);

  const ressourcesPtz = Math.max(rfrPtz, coutOperation / 9);
  const coef = coefFamilial[Math.min(personnes, 5) - 1];
  const revenuRetenu = ressourcesPtz / coef;
  const trancheIdx = seuilsTranches[zonePtz].findIndex((s) => revenuRetenu <= s);
  const eligiblePtz = trancheIdx !== -1;
  const plafondOp = plafondsOperation[zonePtz][Math.min(personnes, 5) - 1];
  const montantPtz = eligiblePtz ? typeLogement.q[trancheIdx] * Math.min(coutOperation, plafondOp) : 0;
  const ptzActif = eligiblePtz && inclurePtz;

  const capitalEffectif = ptzActif ? Math.max(0, capital - montantPtz) : capital;
  const t = taux / 100 / 12;
  const n = duree * 12;
  const mensualite = t === 0 ? capitalEffectif / n : (capitalEffectif * t) / (1 - Math.pow(1 + t, -n));
  const coutCredit = mensualite * n - capitalEffectif;

  const t2 = taux2 / 100 / 12;
  const n2 = duree2 * 12;
  const capacite = t2 === 0 ? mensuMax * n2 : (mensuMax * (1 - Math.pow(1 + t2, -n2))) / t2;
  const budgetTotal = capacite + apport + (ptzActif ? montantPtz : 0);

  return (
    <section id="simulateurs" className="py-24 lg:py-32 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="calculators-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-16"
      >
        <p className="font-sub text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-terra mb-4">Simulateurs gratuits</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight max-w-2xl">
          Chiffrez votre projet en <em className="italic text-terra">30 secondes</em>
        </h2>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white border border-border rounded-xl p-8 lg:p-12 shadow-soft"
          data-testid="calc-loan-card"
        >
          <div className="flex items-center gap-3 mb-10">
            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-terra/10 text-terra">
              <Calculator className="w-5 h-5" />
            </span>
            <h3 className="font-sub font-bold text-xl">Mensualité de prêt</h3>
          </div>
          <div className="space-y-8 mb-10">
            <Slider label="Montant emprunté" value={capital} min={50000} max={600000} step={5000} onChange={setCapital} format={(v) => fmt.format(v)} testid="calc-capital-slider" />
            <Slider label="Taux d'intérêt" value={taux} min={0.5} max={6} step={0.05} onChange={setTaux} format={(v) => `${v.toFixed(2)} %`} testid="calc-rate-slider" />
            <Slider label="Durée" value={duree} min={5} max={30} step={1} onChange={setDuree} format={(v) => `${v} ans`} testid="calc-duration-slider" />
          </div>
          {eligiblePtz && (
            <label className="flex items-center gap-3 mb-8 -mt-2 cursor-pointer" data-testid="calc-loan-ptz-toggle">
              <input type="checkbox" checked={inclurePtz} onChange={(e) => setInclurePtz(e.target.checked)} className="w-4 h-4 accent-terra" data-testid="calc-loan-ptz-checkbox" />
              <span className="font-sub text-sm text-ink/70">Déduire mon PTZ estimé ({fmt.format(montantPtz)}) du montant à financer à taux classique</span>
            </label>
          )}
          <div className="border-t border-border pt-8 grid grid-cols-2 gap-6">
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Mensualité</p>
              <p className="font-display text-3xl lg:text-4xl text-terra" data-testid="calc-monthly-result">{fmt.format(mensualite)}</p>
              <p className="text-xs text-stone2 mt-1">par mois, hors assurance{ptzActif ? " — PTZ déduit, remboursé séparément à 0 %" : ""}</p>
            </div>
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Coût du crédit</p>
              <p className="font-display text-3xl lg:text-4xl" data-testid="calc-cost-result">{fmt.format(coutCredit)}</p>
              <p className="text-xs text-stone2 mt-1">intérêts sur {duree} ans</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white border border-border rounded-xl p-8 lg:p-12 shadow-soft"
          data-testid="calc-budget-card"
        >
          <div className="flex items-center gap-3 mb-10">
            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-olive/10 text-olive">
              <Wallet className="w-5 h-5" />
            </span>
            <h3 className="font-sub font-bold text-xl">Budget finançable</h3>
          </div>
          <div className="space-y-8 mb-10">
            <Slider label="Mensualité maximale" value={mensuMax} min={500} max={4000} step={50} onChange={setMensuMax} format={(v) => fmt.format(v)} testid="calc-mensu-slider" />
            <Slider label="Taux d'intérêt" value={taux2} min={0.5} max={6} step={0.05} onChange={setTaux2} format={(v) => `${v.toFixed(2)} %`} testid="calc-rate2-slider" />
            <Slider label="Durée" value={duree2} min={5} max={30} step={1} onChange={setDuree2} format={(v) => `${v} ans`} testid="calc-duration2-slider" />
            <Slider label="Apport personnel" value={apport} min={0} max={200000} step={5000} onChange={setApport} format={(v) => fmt.format(v)} testid="calc-apport-slider" />
          </div>
          {eligiblePtz && (
            <label className="flex items-center gap-3 mb-8 -mt-2 cursor-pointer" data-testid="calc-budget-ptz-toggle">
              <input type="checkbox" checked={inclurePtz} onChange={(e) => setInclurePtz(e.target.checked)} className="w-4 h-4 accent-terra" data-testid="calc-budget-ptz-checkbox" />
              <span className="font-sub text-sm text-ink/70">Ajouter mon PTZ estimé ({fmt.format(montantPtz)}) au budget total</span>
            </label>
          )}
          <div className="border-t border-border pt-8 grid grid-cols-2 gap-6">
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Capacité d'emprunt</p>
              <p className="font-display text-3xl lg:text-4xl text-olive" data-testid="calc-capacity-result">{fmt.format(capacite)}</p>
              <p className="text-xs text-stone2 mt-1">sur {duree2} ans</p>
            </div>
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Budget total</p>
              <p className="font-display text-3xl lg:text-4xl" data-testid="calc-total-result">{fmt.format(budgetTotal)}</p>
              <p className="text-xs text-stone2 mt-1">{ptzActif ? "apport et PTZ inclus" : "apport inclus"}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white border border-border rounded-xl p-8 lg:p-12 shadow-soft lg:col-span-2"
          data-testid="calc-ptz-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-terra/10 text-terra">
              <Percent className="w-5 h-5" />
            </span>
            <h3 className="font-sub font-bold text-xl">Prêt à taux zéro (PTZ) — logement neuf</h3>
          </div>
          <p className="text-sm text-ink/60 leading-relaxed mb-10 max-w-2xl">
            Depuis avril 2025, le PTZ finance les logements neufs partout en France, jusqu'à fin 2027. Estimez le montant auquel vous avez droit selon le type de logement, votre zone, votre foyer et vos revenus. <span className="font-sub font-bold text-terra">Le résultat alimente automatiquement les simulateurs de mensualité et de budget ci-dessus.</span>
          </p>
          <div className="grid lg:grid-cols-2 gap-x-12 gap-y-8 mb-10">
            <ToggleGroup
              label="Type de logement"
              options={typesLogement}
              value={typeLogement}
              onChange={setTypeLogement}
              activeClass="bg-terra text-white border-terra"
              testidPrefix="calc-logement"
            />
            <ToggleGroup
              label="Zone du projet"
              options={zonesPtz}
              value={zonePtz}
              onChange={setZonePtz}
              activeClass="bg-terra text-white border-terra"
              testidPrefix="calc-zone"
            />
            <Slider label="Personnes dans le foyer" value={personnes} min={1} max={8} step={1} onChange={setPersonnes} format={(v) => (v >= 5 ? "5 et +" : `${v}`)} testid="calc-personnes-slider" />
            <Slider label="Revenu fiscal de référence (N-2)" value={rfrPtz} min={10000} max={150000} step={1000} onChange={setRfrPtz} format={(v) => fmt.format(v)} testid="calc-rfr-slider" />
            <Slider label="Coût total de l'opération" value={coutOperation} min={50000} max={600000} step={5000} onChange={setCoutOperation} format={(v) => fmt.format(v)} testid="calc-operation-slider" />
          </div>
          <div className="border-t border-border pt-8 grid grid-cols-2 gap-6">
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">PTZ estimé</p>
              {eligiblePtz ? (
                <>
                  <p className="font-display text-3xl lg:text-4xl text-terra" data-testid="calc-ptz-result">{fmt.format(montantPtz)}</p>
                  <p className="text-xs text-stone2 mt-1">à 0 %, en complément de votre prêt principal</p>
                </>
              ) : (
                <>
                  <p className="font-display text-3xl lg:text-4xl text-stone2" data-testid="calc-ptz-result">Non éligible</p>
                  <p className="text-xs text-stone2 mt-1">revenus au-dessus du plafond de la zone</p>
                </>
              )}
            </div>
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Votre tranche</p>
              <p className="font-display text-3xl lg:text-4xl" data-testid="calc-ptz-tranche">
                {eligiblePtz ? `Tranche ${trancheIdx + 1}` : "—"}
              </p>
              <p className="text-xs text-stone2 mt-1">
                {eligiblePtz ? `quotité de ${typeLogement.q[trancheIdx] * 100} % du coût retenu (${typeLogement.label.toLowerCase()})` : "barème 2025-2027, logement neuf"}
              </p>
            </div>
          </div>
          <a
            href="https://www.anil.org/outils/outils-de-calcul/votre-pret-a-taux-zero/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calc-ptz-anil-link"
            className="group mt-8 inline-flex items-center gap-2 font-sub text-sm font-medium text-olive hover:text-terra transition-colors duration-200"
          >
            Simulateur officiel détaillé sur anil.org
            <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <span className="mx-3 text-stone2">·</span>
          <a
            href="/guides/ptz-pret-a-taux-zero"
            data-testid="calc-ptz-guide-link"
            className="group mt-8 inline-flex items-center gap-2 font-sub text-sm font-medium text-terra hover:text-terra-dark transition-colors duration-200"
          >
            Lire le guide : PTZ, mode d'emploi
            <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white border border-border rounded-xl p-8 lg:p-12 shadow-soft"
          data-testid="calc-m2-card"
        >
          <div className="flex items-center gap-3 mb-10">
            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-terra/10 text-terra">
              <Ruler className="w-5 h-5" />
            </span>
            <h3 className="font-sub font-bold text-xl">Coût de construction au m²</h3>
          </div>
          <div className="space-y-8 mb-10">
            <Slider label="Surface habitable" value={surface} min={50} max={250} step={5} onChange={setSurface} format={(v) => `${v} m²`} testid="calc-surface-slider" />
            <ToggleGroup
              label="Niveau de gamme"
              options={gammes}
              value={gamme}
              onChange={setGamme}
              activeClass="bg-terra text-white border-terra"
              testidPrefix="calc-gamme"
            />
          </div>
          <div className="border-t border-border pt-8 grid grid-cols-2 gap-6">
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Coût estimé</p>
              <p className="font-display text-3xl lg:text-4xl text-terra" data-testid="calc-m2-result">{fmt.format(surface * gamme.prix)}</p>
              <p className="text-xs text-stone2 mt-1">hors terrain et frais annexes</p>
            </div>
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Prix au m²</p>
              <p className="font-display text-3xl lg:text-4xl" data-testid="calc-m2-price">{fmt.format(gamme.prix)}</p>
              <p className="text-xs text-stone2 mt-1">gamme {gamme.label.toLowerCase()}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white border border-border rounded-xl p-8 lg:p-12 shadow-soft"
          data-testid="calc-notary-card"
        >
          <div className="flex items-center gap-3 mb-10">
            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-olive/10 text-olive">
              <Scale className="w-5 h-5" />
            </span>
            <h3 className="font-sub font-bold text-xl">Frais de notaire</h3>
          </div>
          <div className="space-y-8 mb-10">
            <Slider label="Prix d'achat" value={prixAchat} min={50000} max={800000} step={5000} onChange={setPrixAchat} format={(v) => fmt.format(v)} testid="calc-prix-slider" />
            <ToggleGroup
              label="Type de bien"
              options={typesBien}
              value={typeBien}
              onChange={setTypeBien}
              activeClass="bg-olive text-white border-olive"
              testidPrefix="calc-bien"
            />
          </div>
          <div className="border-t border-border pt-8 grid grid-cols-2 gap-6">
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Frais estimés</p>
              <p className="font-display text-3xl lg:text-4xl text-olive" data-testid="calc-notary-result">{fmt.format(prixAchat * typeBien.taux)}</p>
              <p className="text-xs text-stone2 mt-1">≈ {(typeBien.taux * 100).toFixed(1).replace(".", ",")} % du prix</p>
            </div>
            <div>
              <p className="font-sub text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Budget à prévoir</p>
              <p className="font-display text-3xl lg:text-4xl" data-testid="calc-notary-total">{fmt.format(prixAchat * (1 + typeBien.taux))}</p>
              <p className="text-xs text-stone2 mt-1">prix + frais</p>
            </div>
          </div>
          <a
            href="https://www.anil.org/outils/outils-de-calcul/frais-dacquisition-dits-frais-de-notaire/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calc-notary-anil-link"
            className="group mt-8 inline-flex items-center gap-2 font-sub text-sm font-medium text-olive hover:text-terra transition-colors duration-200"
          >
            Calcul officiel détaillé sur anil.org
            <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>
      <p className="text-xs text-stone2 mt-8 max-w-2xl" data-testid="calc-disclaimer">
        Simulations indicatives, hors assurance emprunteur. PTZ : barème du logement neuf en vigueur (2025-2027), sous réserve d'éligibilité (résidence principale, primo-accession). Pour un chiffrage précis, rapprochez-vous de votre banque ou d'un courtier.
      </p>
    </section>
  );
}
